import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, Loader2, Package, RefreshCw, Search, FileDown, Upload, FileSpreadsheet, AlertTriangle,
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGE = {
  'Order Created':  'bg-slate-200 text-slate-700 border-slate-300',
  'Pending':        'bg-red-100 text-red-700 border-red-200',
  'Order Picked':   'bg-blue-100 text-blue-700 border-blue-200',
  'Dispatched':     'bg-orange-100 text-orange-700 border-orange-200',
  'Delivered':      'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const LIFECYCLE_FILTERS = [
  'All', 'Order Created', 'Pending', 'Order Picked', 'Dispatched', 'Delivered',
];

const fmtCurrency = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const x = Number(n);
  if (!isFinite(x)) return '—';
  return '₹' + x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtTs = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    // 28-Apr-2026 02:54 AM
    const day = String(d.getDate()).padStart(2, '0');
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    const yr = d.getFullYear();
    let hr = d.getHours();
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${yr} ${hr}:${min} ${ampm}`;
  } catch (e) {
    return null;
  }
};

function TimelineCell({ row }) {
  const created = fmtTs(row.order_created_at);
  const dispatched = fmtTs(row.dispatched_at);
  const delivered = fmtTs(row.delivered_at);
  return (
    <div className="text-[10px] leading-tight space-y-0.5 min-w-[140px]" data-testid={`timeline-${row.uid}`}>
      <div className="flex items-center gap-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
        <span className="text-slate-500 font-semibold uppercase tracking-wide">Created:</span>
        <span className="text-slate-700">{created || '—'}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className={'inline-block w-1.5 h-1.5 rounded-full ' + (dispatched ? 'bg-orange-500' : 'bg-slate-200')} />
        <span className="text-slate-500 font-semibold uppercase tracking-wide">Dispatched:</span>
        <span className={dispatched ? 'text-slate-700' : 'text-slate-300'}>{dispatched || '—'}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className={'inline-block w-1.5 h-1.5 rounded-full ' + (delivered ? 'bg-emerald-500' : 'bg-slate-200')} />
        <span className="text-slate-500 font-semibold uppercase tracking-wide">Delivered:</span>
        <span className={delivered ? 'text-emerald-700 font-semibold' : 'text-slate-300'}>{delivered || '—'}</span>
      </div>
      {row.delivered_by_name && delivered && (
        <div className="text-[9px] text-emerald-600 pl-2.5">by {row.delivered_by_name}</div>
      )}
    </div>
  );
}

function StatusBadge({ status, testid }) {
  const cls = STATUS_BADGE[status] || 'bg-slate-100 text-slate-700';
  return (
    <span
      data-testid={testid}
      className={'inline-block px-2 py-0.5 text-xs font-semibold rounded border whitespace-nowrap ' + cls}
    >
      {status}
    </span>
  );
}

function BulkDeliverDialog({ open, onClose, token, onComplete }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null); setResult(null); setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(API + '/sales-orders/bulk-mark-delivered/template.xlsx', {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'bulk_mark_delivered_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a file first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(API + '/sales-orders/bulk-mark-delivered', fd, {
        headers: { Authorization: 'Bearer ' + token },
      });
      setResult(res.data);
      const ok = res.data.success_count || 0;
      const al = res.data.already_delivered_count || 0;
      const sk = res.data.skipped_count || 0;
      if (ok > 0 || al > 0) {
        toast.success(`Marked ${ok} delivered (${al} already-delivered, ${sk} skipped)`);
      } else if (sk > 0) {
        toast.warning(`No rows marked — ${sk} skipped (see report)`);
      }
      if (onComplete) onComplete();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSkipped = async () => {
    if (!result || !result.skipped_rows || !result.skipped_rows.length) return;
    try {
      const res = await axios.post(
        API + '/sales-orders/bulk-mark-delivered/download-skipped',
        { skipped_rows: result.skipped_rows },
        { headers: { Authorization: 'Bearer ' + token }, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'bulk_mark_delivered_skipped_rows.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download skipped report');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-deliver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Upload size={18} /> Bulk Mark Delivered
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-600 bg-slate-50 border rounded p-3">
            Upload a CSV / Excel with columns <code className="px-1 bg-white border rounded">Order Number</code> and <code className="px-1 bg-white border rounded">Product ID</code>.
            Only <strong>Dispatched</strong> lines (with linked Invoice or Delivery Challan) will be marked Delivered. Other rows are <strong>skipped</strong> with a reason — the file will not be rejected.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
              data-testid="bulk-deliver-template-btn"
            >
              <FileSpreadsheet size={14} /> Download Template
            </Button>
            <CsvDownloadButton onDownload={handleDownloadTemplate} testId="bulk-deliver-template-csv" label="Template CSV" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }}
              className="text-sm flex-1 min-w-0"
              data-testid="bulk-deliver-file-input"
            />
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
              data-testid="bulk-deliver-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload &amp; Mark Delivered
            </Button>
          </div>

          {result && (
            <div className="space-y-3" data-testid="bulk-deliver-result">
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-emerald-700" data-testid="result-success-count">{result.success_count || 0}</div>
                  <div className="text-[10px] text-emerald-700 uppercase">Marked</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-700" data-testid="result-already-count">{result.already_delivered_count || 0}</div>
                  <div className="text-[10px] text-blue-700 uppercase">Already Delivered</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-amber-700" data-testid="result-skipped-count">{result.skipped_count || 0}</div>
                  <div className="text-[10px] text-amber-700 uppercase">Skipped</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-slate-700">{result.total_rows || 0}</div>
                  <div className="text-[10px] text-slate-700 uppercase">Total Rows</div>
                </div>
              </div>
              {result.skipped_rows && result.skipped_rows.length > 0 && (
                <div className="border rounded">
                  <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border-b">
                    <div className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                      <AlertTriangle size={14} /> Skipped Rows ({result.skipped_rows.length})
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDownloadSkipped}
                      className="gap-1 h-7 text-xs"
                      data-testid="bulk-deliver-download-skipped-btn"
                    >
                      <FileDown size={12} /> Download
                    </Button>
                    <CsvDownloadButton onDownload={handleDownloadSkipped} testId="bulk-deliver-skipped-csv" className="h-7 text-xs" />
                  </div>
                  <div className="max-h-48 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white border-b">
                        <tr>
                          <th className="text-left px-2 py-1 font-semibold">#</th>
                          <th className="text-left px-2 py-1 font-semibold">Order</th>
                          <th className="text-left px-2 py-1 font-semibold">Product</th>
                          <th className="text-left px-2 py-1 font-semibold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {result.skipped_rows.map((r) => (
                          <tr key={r.row}>
                            <td className="px-2 py-1 text-slate-500">{r.row}</td>
                            <td className="px-2 py-1 font-mono text-slate-700">{r.order_number}</td>
                            <td className="px-2 py-1 font-mono text-slate-700">{r.product_id}</td>
                            <td className="px-2 py-1 text-red-600">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="bulk-deliver-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesOrdersDashboard() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [delivering, setDelivering] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'All') params.status = statusFilter;
      const res = await axios.get(API + '/sales-orders/lines', {
        headers: { Authorization: 'Bearer ' + token },
        params,
      });
      setLines((res.data && res.data.lines) || []);
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || 'Failed to load Sales Orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkDelivered = useCallback(async (line) => {
    if (!window.confirm(`Mark "${line.product_name}" (${line.order_number}) as Delivered?`)) return;
    setDelivering(line.uid);
    try {
      const res = await axios.post(
        `${API}/sales-orders/${line.order_id}/items/${line.uid}/mark-delivered`,
        {},
        { headers: { Authorization: 'Bearer ' + token } },
      );
      toast.success(res.data.already_delivered ? 'Line was already Delivered' : `${line.product_name} marked Delivered`);
      fetchData();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || 'Failed to mark delivered');
    } finally {
      setDelivering(null);
    }
  }, [fetchData, token]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(API + '/sales-orders/export.xlsx', {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((r) =>
      (r.franchise_id || '').toLowerCase().indexOf(q) !== -1
      || (r.franchise_name || '').toLowerCase().indexOf(q) !== -1
      || (r.business_segment || '').toLowerCase().indexOf(q) !== -1
      || (r.order_number || '').toLowerCase().indexOf(q) !== -1
      || (r.product_id || '').toLowerCase().indexOf(q) !== -1
      || (r.product_name || '').toLowerCase().indexOf(q) !== -1
      || (r.parent_brand || '').toLowerCase().indexOf(q) !== -1
      || (r.sub_brand || '').toLowerCase().indexOf(q) !== -1
      || (r.category || '').toLowerCase().indexOf(q) !== -1
    );
  }, [lines, search]);

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="sales-orders-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <Package className="w-7 h-7 text-orange-600" />
            Sales Orders
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Franchise-wise lifecycle tracking · Order Created → Picked → Dispatched → Delivered
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Franchise / Segment / Order / Product / Brand"
              className="pl-8 w-72"
              data-testid="sales-orders-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="sales-orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIFECYCLE_FILTERS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
            data-testid="sales-orders-export-btn"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Export Excel
          </Button>
          <CsvDownloadButton onDownload={handleExport} testId="sales-orders-export-csv" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-1"
            data-testid="sales-orders-bulk-deliver-btn"
          >
            <Upload size={14} /> Bulk Mark Delivered
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            disabled={loading}
            data-testid="sales-orders-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sales-orders-summary-tiles">
        {LIFECYCLE_FILTERS.slice(1).map((s) => {
          const count = lines.filter((r) => r.order_status === s).length;
          return (
            <Card key={s} className="border">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase text-slate-500 font-semibold">{s}</div>
                <div className="text-2xl font-bold text-slate-800 mt-1" data-testid={`tile-${s.toLowerCase().replace(/ /g, '-')}`}>
                  {count}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-orders-table">
            <thead className="bg-slate-100 border-b text-xs text-slate-600 uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Franchise ID</th>
                <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Business Segment</th>
                <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Order #</th>
                <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Product ID</th>
                <th className="text-left px-3 py-2 font-semibold">Product Name</th>
                <th className="text-left px-3 py-2 font-semibold">Parent Brand</th>
                <th className="text-left px-3 py-2 font-semibold">Sub Brand</th>
                <th className="text-left px-3 py-2 font-semibold">Category</th>
                <th className="text-right px-3 py-2 font-semibold">Qty</th>
                <th className="text-center px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">MRP</th>
                <th className="text-right px-3 py-2 font-semibold">Outward B2B</th>
                <th className="text-left px-3 py-2 font-semibold">Purchaser</th>
                <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Timeline</th>
                <th className="text-center px-3 py-2 font-semibold whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={15} className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="font-medium">No sales orders match the current filter.</p>
                    <p className="text-xs mt-1">Try clearing the status filter or search.</p>
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const isDelivering = delivering === r.uid;
                return (
                  <tr key={r.uid} className="hover:bg-slate-50" data-testid={`row-${r.uid}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.franchise_id || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.business_segment ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap"
                          data-testid={`segment-${r.uid}`}
                        >
                          {r.business_segment}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.order_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.product_id || '—'}</td>
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.parent_brand || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.sub_brand || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.category || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{r.order_qty}</td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge status={r.order_status} testid={`lifecycle-${r.uid}`} />
                      {(r.linked_invoice_number || r.linked_delivery_challan_number) && (
                        <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                          {r.linked_invoice_number ? 'Inv: ' + r.linked_invoice_number : ''}
                          {r.linked_delivery_challan_number ? ' DC: ' + r.linked_delivery_challan_number : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.mrp)}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.outward_b2b)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.order_purchaser || '—'}</td>
                    <td className="px-3 py-2"><TimelineCell row={r} /></td>
                    <td className="px-3 py-2 text-center">
                      {r.order_status === 'Dispatched' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkDelivered(r)}
                          disabled={isDelivering}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                          data-testid={`mark-delivered-${r.uid}`}
                        >
                          {isDelivering ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                          Mark Delivered
                        </Button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <BulkDeliverDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        token={token}
        onComplete={fetchData}
      />
    </div>
  );
}
